import { Fragment } from "react";
import { Check, Info, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";

const REPORT_LANGUAGE_ORDER = ["id", "en"];
const PLAN_NAME_KEYS = {
  free: "pricing.plan.free",
  starter: "pricing.plan.starter",
  pro: "pricing.plan.pro",
  business: "pricing.plan.business",
};

function getPlanLabel(plan, t) {
  const custom = String(plan?.name || "").trim();
  if (custom) return custom;
  const key = PLAN_NAME_KEYS[plan?.id];
  return key ? t(key) : String(plan?.id || "");
}

function formatNumber(value, locale) {
  return Number(value || 0).toLocaleString(locale);
}

function formatFileLimit(mb) {
  const value = Number(mb || 0);
  if (value <= 0) return "50 MB";
  if (value >= 1024 && value % 1024 === 0) return `${value / 1024} GB`;
  return `${value} MB`;
}

function formatDurationLimit(seconds, locale, t) {
  const value = Number(seconds || 0);
  if (value <= 0) return t("pricing.compare.value.unlimited");
  const minutes = Math.round((value / 60) * 10) / 10;
  const minutesText = Number.isInteger(minutes)
    ? Number(minutes).toLocaleString(locale)
    : minutes.toLocaleString(locale, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
  return t("pricing.compare.value.minutes", { value: minutesText });
}

function getPlanFeatureMap(plan) {
  return plan?.feature_overrides && typeof plan.feature_overrides === "object"
    ? plan.feature_overrides
    : {};
}

function getReportLanguagesLabel(plan, t) {
  const featureMap = getPlanFeatureMap(plan);
  const raw = featureMap?.report_languages;
  const normalized = Array.isArray(raw)
    ? raw
        .map((lang) => String(lang || "").trim().toLowerCase())
        .filter((lang) => REPORT_LANGUAGE_ORDER.includes(lang))
    : [];
  const unique = Array.from(new Set(normalized));
  if (!unique.length) {
    return featureMap?.report_bilingual === false
      ? t("pricing.lang.id")
      : `${t("pricing.lang.id")}, ${t("pricing.lang.en")}`;
  }
  if (!unique.includes("id")) unique.unshift("id");
  const ordered = REPORT_LANGUAGE_ORDER.filter((lang) => unique.includes(lang));
  return ordered.map((lang) => t(`pricing.lang.${lang}`)).join(", ");
}

function boolCell(value) {
  return value ? (
    <span className="inline-flex items-center justify-center text-verdict-real">
      <Check className="w-4 h-4" />
    </span>
  ) : (
    <span className="inline-flex items-center justify-center text-muted-foreground/60">
      <X className="w-4 h-4" />
    </span>
  );
}

function textOrNA(value, t) {
  if (value === undefined || value === null || value === "") {
    return t("pricing.compare.value.notAvailable");
  }
  return value;
}

export default function PlanComparisonTable({
  plans = [],
  currentPlan = "free",
  processingPlanId = null,
  onPlanSelect,
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";

  const visiblePlans = [...plans]
    .filter((p) => p?.is_active !== false && p?.is_public !== false)
    .sort((a, b) => Number(a?.sort_order ?? 9999) - Number(b?.sort_order ?? 9999));
  const rankMap = visiblePlans.reduce((acc, plan, idx) => {
    acc[plan.id] = idx;
    return acc;
  }, {});
  const currentRank = rankMap[currentPlan] ?? -1;

  if (!visiblePlans.length) return null;

  const sections = [
    {
      title: t("pricing.sec.quota"),
      rows: [
        {
          label: t("pricing.compare.row.monthlyLimit"),
          value: (plan) => formatNumber(plan?.limit, locale),
        },
        {
          label: t("pricing.compare.row.dailyLimit"),
          value: (plan) => formatNumber(plan?.daily_limit, locale),
        },
        {
          label: t("pricing.compare.row.fileLimit"),
          value: (plan) => formatFileLimit(plan?.file_limit_mb),
        },
        {
          label: t("pricing.compare.row.cooldown"),
          value: (plan) =>
            Number(plan?.cooldown_s || 0) > 0
              ? t("pricing.compare.value.seconds", { count: Number(plan.cooldown_s || 0) })
              : t("pricing.compare.value.notAvailable"),
        },
        {
          label: t("pricing.compare.row.maxVideoDuration"),
          value: (plan) => formatDurationLimit(plan?.max_video_duration_s, locale, t),
        },
        {
          label: t("pricing.compare.row.maxAudioDuration"),
          value: (plan) => formatDurationLimit(plan?.max_audio_duration_s, locale, t),
        },
      ],
    },
    {
      title: t("pricing.sec.detect"),
      rows: [
        {
          label: t("pricing.feat.detect.image"),
          bool: (plan) => !!plan?.media_access?.image,
        },
        {
          label: t("pricing.feat.detect.video"),
          bool: (plan) => !!plan?.media_access?.video,
        },
        {
          label: t("pricing.feat.detect.audio"),
          bool: (plan) => !!plan?.media_access?.audio,
        },
      ],
    },
    {
      title: t("pricing.sec.report"),
      rows: [
        {
          label: t("pricing.feat.report.score"),
          bool: (plan) => !!getPlanFeatureMap(plan)?.report_score,
        },
        {
          label: t("pricing.compare.row.aiExplain"),
          tooltipKey: "aiExplain",
          value: (plan) => {
            const aiLevel = String(
              getPlanFeatureMap(plan)?.ai_explanation_level || "basic",
            ).toLowerCase();
            if (aiLevel === "full") return t("pricing.compare.value.full");
            if (aiLevel === "basic") return t("pricing.compare.value.basic");
            return t("pricing.compare.value.notAvailable");
          },
        },
        {
          label: t("pricing.compare.row.reportLanguages"),
          value: (plan) => getReportLanguagesLabel(plan, t),
        },
        {
          label: t("pricing.feat.report.technical"),
          tooltipKey: "reportTechnical",
          bool: (plan) => !!getPlanFeatureMap(plan)?.report_technical,
        },
        {
          label: t("pricing.feat.report.pdf"),
          bool: (plan) => !!getPlanFeatureMap(plan)?.report_pdf_download,
        },
      ],
    },
    {
      title: t("pricing.sec.storage"),
      rows: [
        {
          label: t("pricing.feat.storage.history"),
          bool: (plan) => !!getPlanFeatureMap(plan)?.storage_history,
        },
        {
          label: t("pricing.feat.storage.upload"),
          bool: (plan) => !!getPlanFeatureMap(plan)?.storage_file_upload,
        },
        {
          label: t("pricing.compare.row.gracePeriod"),
          value: (plan) => {
            const days = Number(getPlanFeatureMap(plan)?.storage_grace_period_days || 0);
            return days > 0
              ? t("pricing.compare.value.days", { count: days })
              : t("pricing.compare.value.notAvailable");
          },
        },
        {
          label: t("pricing.feat.storage.credits"),
          bool: (plan) => !!getPlanFeatureMap(plan)?.credits_topup_enabled,
        },
      ],
    },
    {
      title: t("pricing.sec.support"),
      rows: [
        {
          label: t("pricing.feat.support.center"),
          bool: (plan) => !!getPlanFeatureMap(plan)?.support_help_center,
        },
        {
          label: t("pricing.compare.row.emailSupport"),
          value: (plan) => {
            const hours = Number(getPlanFeatureMap(plan)?.support_email_sla_hours || 0);
            return hours > 0
              ? t("pricing.feat.support.emailHours", { hours })
              : t("pricing.compare.value.notAvailable");
          },
        },
        {
          label: t("pricing.feat.support.api"),
          bool: (plan) => !!getPlanFeatureMap(plan)?.support_api_access,
        },
      ],
    },
  ];

  return (
    <div className="rounded-md border border-border bg-card/40 p-4 md:p-5" data-testid="pricing-compare-table">
      <h3 className="font-heading text-xl md:text-2xl font-bold mb-1">
        {t("pricing.compare.detailTitle")}
      </h3>
      <p className="text-sm md:text-base text-muted-foreground mb-4">
        {t("pricing.compare.detailSubtitle")}
      </p>

      <div className="overflow-x-auto">
        <table className="min-w-full text-sm rounded-sm border border-border overflow-hidden">
          <thead className="bg-secondary/25">
            <tr className="border-b border-border">
              <th className="px-4 py-3 text-left text-sm font-heading font-semibold text-foreground min-w-[220px]">
                {t("pricing.compare.col.feature", { defaultValue: "Fitur" })}
              </th>
              {visiblePlans.map((plan) => (
                <th
                  key={plan.id}
                  className="px-4 py-3 text-center align-middle text-base font-heading font-semibold text-foreground min-w-[150px]"
                >
                  {getPlanLabel(plan, t)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <Fragment key={section.title}>
                <tr className="border-b border-border/60 bg-secondary/10">
                  <td
                    colSpan={visiblePlans.length + 1}
                    className="px-4 py-2.5 text-sm md:text-base font-heading font-semibold tracking-wide text-foreground/90"
                  >
                    {section.title}
                  </td>
                </tr>
                {section.rows.map((row) => (
                  <tr key={`${section.title}-${row.label}`} className="border-b border-border/40">
                    <td className="px-4 py-2.5 text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        {row.label}
                        {row.tooltipKey && (
                          <TooltipProvider delayDuration={100}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  className="inline-flex items-center text-muted-foreground/70 hover:text-foreground focus:outline-none focus:ring-1 focus:ring-brand-primary rounded-sm"
                                  aria-label={t("pricing.compare.tooltip.label")}
                                >
                                  <Info className="w-3.5 h-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="top"
                                align="start"
                                sideOffset={8}
                                className="z-[80] max-w-[300px] whitespace-normal break-words leading-snug text-[12px] px-2.5 py-2"
                              >
                                {row.tooltipKey === "aiExplain" ? (
                                  <div className="space-y-1">
                                    <p>
                                      <span className="font-semibold">{t("pricing.compare.tooltip.ai.noneLabel")}</span>{" "}
                                      {t("pricing.compare.tooltip.ai.noneDesc")}
                                    </p>
                                    <p>
                                      <span className="font-semibold">{t("pricing.compare.tooltip.ai.basicLabel")}</span>{" "}
                                      {t("pricing.compare.tooltip.ai.basicDesc")}
                                    </p>
                                    <p>
                                      <span className="font-semibold">{t("pricing.compare.tooltip.ai.fullLabel")}</span>{" "}
                                      {t("pricing.compare.tooltip.ai.fullDesc")}
                                    </p>
                                  </div>
                                ) : (
                                  t("pricing.compare.tooltip.reportTechnical")
                                )}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </span>
                    </td>
                    {visiblePlans.map((plan) => (
                      <td
                        key={`${section.title}-${row.label}-${plan.id}`}
                        className="px-4 py-2.5 text-center align-middle"
                      >
                        {row.bool
                          ? boolCell(Boolean(row.bool(plan)))
                          : <span className="block text-center">{textOrNA(row.value?.(plan), t)}</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
            <tr className="border-b border-border/40 bg-secondary/10">
              <td className="px-4 py-3" />
              {visiblePlans.map((plan) => {
                const planId = String(plan?.id || "");
                const isCurrent = planId === currentPlan;
                const isDowngrade = !isCurrent && (rankMap[planId] ?? 0) < currentRank;
                const isBusy = processingPlanId === planId;
                const planLabel = getPlanLabel(plan, t);
                const ctaLabel = isCurrent
                  ? t("pricing.cta.current")
                  : isDowngrade
                    ? `${t("pricing.cta.downgrade")} ${planLabel}`
                    : `${t("pricing.cta.upgrade")} ${planLabel}`;

                return (
                  <td key={`compare-action-${planId}`} className="px-4 py-3 text-center">
                    <Button
                      size="sm"
                      variant={isCurrent ? "outline" : "default"}
                      disabled={isCurrent || isBusy}
                      onClick={() => onPlanSelect?.(plan)}
                      className={`w-full rounded-sm text-xs ${isCurrent ? "" : "bg-brand-primary hover:bg-brand-primary/90 text-white"}`}
                      data-testid={`compare-cta-${planId}`}
                    >
                      {isBusy ? t("common.loading") : ctaLabel}
                    </Button>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
